import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import ServiceAreaMap from '../components/map/ServiceAreaMap';
import { 
  Phone, Calendar, MapPin, Plane, Smartphone, ChevronRight
} from 'lucide-react';

const AreasPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      metaTitle: "Zones Desservies — Taxi Grand Montréal | MTL Taxi 438-600-4440",
      metaDesc: "MTL Taxi dessert l'ensemble du Grand Montréal 24h/24 : centre-ville, Plateau, Rosemont, Laval, Longueuil, Rive-Sud, Ouest-de-l'Île et aéroport YUL. Appelez le 438-600-4440.",
      title: "Zones desservies — MTL Taxi dans tout le Grand Montréal",
      intro: "MTL Taxi assure le transport de personnes dans l'ensemble du Grand Montréal et ses environs, 24 heures sur 24, 7 jours sur 7, 365 jours par an. Que vous soyez au centre-ville, en banlieue ou que vous ayez besoin d'une navette vers l'aéroport Trudeau (YUL), un chauffeur MTL Taxi peut venir vous chercher où que vous soyez dans notre zone de couverture.",
      mapTitle: "Notre zone de couverture",
      mainAreasTitle: "Principales zones desservies",
      
      areas: [
        {
          name: "Montréal Centre",
          desc: "MTL Taxi couvre tous les quartiers centraux de l'île de Montréal. C'est notre zone de service principale — temps de réponse optimal, disponibilité maximale.",
          neighborhoods: [
            { name: "Centre-ville", details: "incluant le Vieux-Montréal, le Quartier des spectacles, la rue Sainte-Catherine" },
            { name: "Le Plateau-Mont-Royal", details: "Mont-Royal, Rachel, Saint-Denis, Laurier" },
            { name: "Rosemont–La Petite-Patrie", details: "Beaubien, Masson, Saint-Zotique" },
            { name: "Hochelaga-Maisonneuve", details: "Ontario Est, Pie-IX, le Stade olympique" },
            { name: "Notre-Dame-de-Grâce (NDG)", details: "Sherbrooke Ouest, Monkland, Décarie" },
            { name: "Côte-des-Neiges", details: "Université de Montréal, chemin de la Côte-des-Neiges" },
            { name: "Verdun", details: "Wellington, LaSalle, les berges du fleuve" },
            { name: "LaSalle", details: "Angrignon, Newman, le boulevard LaSalle" }
          ],
          links: [{ text: "Réserver un taxi au centre-ville", url: siteConfig.bookingUrl, external: true }, { text: "Tarifs", url: "/fares" }]
        },
        {
          name: "Saint-Laurent & Nord de l'île",
          desc: "Zone stratégique pour les déplacements vers l'aéroport Trudeau (YUL), situé à proximité.",
          neighborhoods: [
            { name: "Saint-Laurent", details: "à 10 minutes de l'aéroport YUL" },
            { name: "Cartierville", details: "Laurentides, Lachapelle" },
            { name: "Ahuntsic", details: "Fleury, Sauvé, Henri-Bourassa" },
            { name: "Bordeaux-Cartierville", details: "" },
            { name: "Parc-Extension", details: "Van Horne, Jarry" }
          ],
          links: [{ text: "Navette aéroport depuis Saint-Laurent", url: "/airport" }]
        },
        {
          name: "Laval",
          desc: "MTL Taxi dessert toute la ville de Laval, au nord de Montréal. Idéal pour les résidents de Laval qui se déplacent vers Montréal ou l'aéroport YUL.",
          neighborhoods: [
            { name: "Chomedey", details: "le secteur le plus proche de l'aéroport YUL" },
            { name: "Laval-des-Rapides", details: "proche du pont de la Concorde" },
            { name: "Pont-Viau", details: "accès rapide au nord de Montréal" },
            { name: "Vimont & Auteuil", details: "secteurs résidentiels" },
            { name: "Fabreville", details: "ouest de Laval" },
            { name: "Sainte-Dorothée", details: "proche du pont Laviolette" }
          ],
          estimate: "Estimation taxi Laval ↔ aéroport YUL : 65–80 $",
          links: [{ text: "Voir tous les tarifs", url: "/fares" }]
        },
        {
          name: "Longueuil & Rive-Sud Est",
          desc: "MTL Taxi traverse les ponts pour desservir la Rive-Sud de Montréal.",
          neighborhoods: [
            { name: "Vieux-Longueuil", details: "Saint-Charles, chemin de Chambly" },
            { name: "Saint-Hubert", details: "proche de l'autoroute 30" },
            { name: "Greenfield Park", details: "secteur résidentiel tranquille" },
            { name: "LeMoyne", details: "proche du pont Jacques-Cartier" }
          ],
          estimate: "Estimation taxi Longueuil ↔ aéroport YUL : 70–85 $",
          links: [{ text: "Voir tous les tarifs", url: "/fares" }]
        },
        {
          name: "Ouest-de-l'Île",
          desc: "Secteur résidentiel et commercial à l'ouest de Montréal, proche de l'aéroport Trudeau.",
          neighborhoods: [
            { name: "Dorval", details: "adjacent à l'aéroport YUL, trajet très court" },
            { name: "Pointe-Claire", details: "bord du lac Saint-Louis" },
            { name: "Kirkland", details: "secteur résidentiel" },
            { name: "Beaconsfield", details: "Beaurepaire, bord du fleuve" },
            { name: "Dollard-Des-Ormeaux (DDO)", details: "Sunnybrooke, Des Sources" },
            { name: "Pierrefonds-Roxboro", details: "Gouin, Saint-Charles" }
          ],
          links: [{ text: "Navette aéroport depuis l'Ouest-de-l'Île", url: "/airport" }]
        },
        {
          name: "Rive-Sud Ouest",
          desc: "",
          neighborhoods: [
            { name: "Brossard", details: "DIX30, Panama, secteur commercial majeur" },
            { name: "Saint-Lambert", details: "Victoria, Green, proche du pont Victoria" },
            { name: "Boucherville", details: "autoroute 20, parc industriel" },
            { name: "Châteauguay", details: "Salaberry, bord de la rivière" },
            { name: "La Prairie", details: "boul. Taschereau, secteur résidentiel" }
          ],
          estimate: "Estimation taxi Brossard ↔ aéroport YUL : 70–85 $",
          links: [{ text: "Voir tous les tarifs", url: "/fares" }]
        }
      ],
      
      airportTitle: "Navette aéroport depuis toutes les zones",
      airportDesc: "Peu importe où vous habitez dans le Grand Montréal, MTL Taxi assure votre transfert vers l'aéroport international Montréal-Trudeau (YUL). Tarif fixe pour le centre-ville, taximètre pour les autres zones. Réservez à l'avance pour garantir votre prise en charge.",
      airportTable: [
        { zone: "Centre-ville (forfait fixe)", price: "49,45 $" },
        { zone: "NDG / Verdun / LaSalle", price: "40–50 $" },
        { zone: "Plateau / Rosemont", price: "50–60 $" },
        { zone: "Saint-Laurent", price: "30–40 $" },
        { zone: "Laval", price: "65–80 $" },
        { zone: "Longueuil / Brossard", price: "70–85 $" },
        { zone: "Ouest-de-l'Île (Dorval)", price: "20–30 $" }
      ],
      airportLink: "Voir le tableau complet des tarifs",
      
      notFoundTitle: "Vous ne voyez pas votre secteur ?",
      notFoundDesc: "Notre zone de service s'étend au-delà des secteurs listés ci-dessus. Pour les trajets longue distance ou les destinations hors Grand Montréal, appelez-nous directement pour une estimation personnalisée.",
      
      availableTitle: "Service disponible 24h/24 dans toutes les zones",
      availableDesc: "Quel que soit votre quartier, MTL Taxi est disponible à toute heure — jour, nuit, jours fériés. Nos chauffeurs professionnels connaissent le territoire du Grand Montréal et ses environs.",
      
      callBtn: "Appeler 438-600-4440",
      bookBtn: "Portail de réservation en ligne",
      appStore: "Télécharger l'application — App Store",
      googlePlay: "Télécharger l'application — Google Play",
      
      footerHome: "← Accueil",
      footerAirport: "Aéroport YUL",
      footerFares: "Tarifs",
      footerServices: "Services",
      footerFaq: "FAQ",
      footerContact: "Contact"
    },
    en: {
      metaTitle: "Service Areas — Greater Montreal Taxi | MTL Taxi 438-600-4440",
      metaDesc: "MTL Taxi serves all of Greater Montreal 24/7: downtown, Plateau, Rosemont, Laval, Longueuil, South Shore, West Island and YUL airport. Call 438-600-4440.",
      title: "Service Areas — MTL Taxi Throughout Greater Montreal",
      intro: "MTL Taxi provides passenger transportation throughout Greater Montreal and its surroundings, 24 hours a day, 7 days a week, 365 days a year. Whether you're downtown, in the suburbs or need a shuttle to Trudeau Airport (YUL), an MTL Taxi driver can pick you up wherever you are in our coverage area.",
      mapTitle: "Our coverage area",
      mainAreasTitle: "Main service areas",
      
      areas: [
        {
          name: "Montreal Centre",
          desc: "MTL Taxi covers all central neighborhoods of the island of Montreal. This is our main service area — optimal response time, maximum availability.",
          neighborhoods: [
            { name: "Downtown", details: "including Old Montreal, Quartier des spectacles, Sainte-Catherine Street" },
            { name: "Le Plateau-Mont-Royal", details: "Mont-Royal, Rachel, Saint-Denis, Laurier" },
            { name: "Rosemont–La Petite-Patrie", details: "Beaubien, Masson, Saint-Zotique" },
            { name: "Hochelaga-Maisonneuve", details: "Ontario East, Pie-IX, Olympic Stadium" },
            { name: "Notre-Dame-de-Grâce (NDG)", details: "Sherbrooke West, Monkland, Décarie" },
            { name: "Côte-des-Neiges", details: "Université de Montréal, Côte-des-Neiges Road" },
            { name: "Verdun", details: "Wellington, LaSalle, riverfront" },
            { name: "LaSalle", details: "Angrignon, Newman, LaSalle Boulevard" }
          ],
          links: [{ text: "Book a taxi downtown", url: siteConfig.bookingUrl, external: true }, { text: "Rates", url: "/fares" }]
        },
        {
          name: "Saint-Laurent & North Island",
          desc: "Strategic zone for trips to Trudeau Airport (YUL), located nearby.",
          neighborhoods: [
            { name: "Saint-Laurent", details: "10 minutes from YUL airport" },
            { name: "Cartierville", details: "Laurentides, Lachapelle" },
            { name: "Ahuntsic", details: "Fleury, Sauvé, Henri-Bourassa" },
            { name: "Bordeaux-Cartierville", details: "" },
            { name: "Parc-Extension", details: "Van Horne, Jarry" }
          ],
          links: [{ text: "Airport shuttle from Saint-Laurent", url: "/airport" }]
        },
        {
          name: "Laval",
          desc: "MTL Taxi serves all of Laval, north of Montreal. Ideal for Laval residents traveling to Montreal or YUL airport.",
          neighborhoods: [
            { name: "Chomedey", details: "closest sector to YUL airport" },
            { name: "Laval-des-Rapides", details: "near Concorde Bridge" },
            { name: "Pont-Viau", details: "quick access to north Montreal" },
            { name: "Vimont & Auteuil", details: "residential sectors" },
            { name: "Fabreville", details: "west Laval" },
            { name: "Sainte-Dorothée", details: "near Laviolette Bridge" }
          ],
          estimate: "Taxi estimate Laval ↔ YUL airport: $65–80",
          links: [{ text: "View all rates", url: "/fares" }]
        },
        {
          name: "Longueuil & East South Shore",
          desc: "MTL Taxi crosses bridges to serve Montreal's South Shore.",
          neighborhoods: [
            { name: "Vieux-Longueuil", details: "Saint-Charles, Chambly Road" },
            { name: "Saint-Hubert", details: "near Highway 30" },
            { name: "Greenfield Park", details: "quiet residential area" },
            { name: "LeMoyne", details: "near Jacques-Cartier Bridge" }
          ],
          estimate: "Taxi estimate Longueuil ↔ YUL airport: $70–85",
          links: [{ text: "View all rates", url: "/fares" }]
        },
        {
          name: "West Island",
          desc: "Residential and commercial sector west of Montreal, close to Trudeau Airport.",
          neighborhoods: [
            { name: "Dorval", details: "adjacent to YUL airport, very short trip" },
            { name: "Pointe-Claire", details: "Lake Saint-Louis shore" },
            { name: "Kirkland", details: "residential sector" },
            { name: "Beaconsfield", details: "Beaurepaire, riverfront" },
            { name: "Dollard-Des-Ormeaux (DDO)", details: "Sunnybrooke, Des Sources" },
            { name: "Pierrefonds-Roxboro", details: "Gouin, Saint-Charles" }
          ],
          links: [{ text: "Airport shuttle from West Island", url: "/airport" }]
        },
        {
          name: "West South Shore",
          desc: "",
          neighborhoods: [
            { name: "Brossard", details: "DIX30, Panama, major commercial sector" },
            { name: "Saint-Lambert", details: "Victoria, Green, near Victoria Bridge" },
            { name: "Boucherville", details: "Highway 20, industrial park" },
            { name: "Châteauguay", details: "Salaberry, riverside" },
            { name: "La Prairie", details: "Taschereau Blvd, residential sector" }
          ],
          estimate: "Taxi estimate Brossard ↔ YUL airport: $70–85",
          links: [{ text: "View all rates", url: "/fares" }]
        }
      ],
      
      airportTitle: "Airport shuttle from all zones",
      airportDesc: "No matter where you live in Greater Montreal, MTL Taxi provides your transfer to Montreal-Trudeau International Airport (YUL). Fixed rate for downtown, meter for other zones. Book in advance to guarantee your pickup.",
      airportTable: [
        { zone: "Downtown (fixed rate)", price: "$49.45" },
        { zone: "NDG / Verdun / LaSalle", price: "$40–50" },
        { zone: "Plateau / Rosemont", price: "$50–60" },
        { zone: "Saint-Laurent", price: "$30–40" },
        { zone: "Laval", price: "$65–80" },
        { zone: "Longueuil / Brossard", price: "$70–85" },
        { zone: "West Island (Dorval)", price: "$20–30" }
      ],
      airportLink: "View complete rate table",
      
      notFoundTitle: "Don't see your area?",
      notFoundDesc: "Our service area extends beyond the sectors listed above. For long-distance trips or destinations outside Greater Montreal, call us directly for a personalized estimate.",
      
      availableTitle: "Service available 24/7 in all zones",
      availableDesc: "Whatever your neighborhood, MTL Taxi is available at any time — day, night, holidays. Our professional drivers know the Greater Montreal territory and its surroundings.",
      
      callBtn: "Call 438-600-4440",
      bookBtn: "Online booking portal",
      appStore: "Download the app — App Store",
      googlePlay: "Download the app — Google Play",
      
      footerHome: "← Home",
      footerAirport: "YUL Airport",
      footerFares: "Rates",
      footerServices: "Services",
      footerFaq: "FAQ",
      footerContact: "Contact"
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen pt-20" data-testid="areas-page">
      <SEO 
        title={t.metaTitle}
        description={t.metaDesc}
        canonical={`https://mtl.taxi/${language === 'fr' ? 'fr/zones-desservies' : 'en/service-areas'}`}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MapPin className="w-4 h-4" />
            Grand Montréal
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight leading-tight">
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground text-center mb-8">
            {t.mapTitle}
          </h2>
          <ServiceAreaMap />
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-primary" />
              <span className="text-muted-foreground">{language === 'fr' ? 'Centre principal' : 'Main center'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gray-500" />
              <span className="text-muted-foreground">{language === 'fr' ? 'Zones desservies' : 'Service areas'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-accent" />
              <span className="text-muted-foreground">{language === 'fr' ? 'Aéroport YUL' : 'YUL Airport'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Areas */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground text-center mb-12">
            {t.mainAreasTitle}
          </h2>

          {t.areas.map((area, index) => (
            <div key={index} className={`mb-12 ${index % 2 === 0 ? '' : ''}`}>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-primary mb-4">
                {area.name}
              </h3>
              {area.desc && (
                <p className="text-muted-foreground text-lg mb-4">{area.desc}</p>
              )}
              <ul className="space-y-2 mb-4">
                {area.neighborhoods.map((n, nIndex) => (
                  <li key={nIndex} className="text-foreground">
                    <strong>{n.name}</strong>
                    {n.details && <span className="text-muted-foreground"> — {n.details}</span>}
                  </li>
                ))}
              </ul>
              {area.estimate && (
                <p className="text-primary font-semibold mb-4">{area.estimate}</p>
              )}
              {area.links && (
                <div className="flex flex-wrap gap-4">
                  {area.links.map((link, lIndex) => (
                    link.external ? (
                      <a key={lIndex} href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
                        → {link.text}
                      </a>
                    ) : (
                      <Link key={lIndex} to={getRoute(link.url)} className="text-primary font-semibold hover:underline">
                        → {link.text}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Airport Table */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Plane className="w-8 h-8 text-primary" />
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
              {t.airportTitle}
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-8">{t.airportDesc}</p>

          <Card className="border-0 shadow-lg overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary text-white">
                  <TableHead className="font-bold text-white">Zone</TableHead>
                  <TableHead className="font-bold text-white text-right">{language === 'fr' ? 'Estimation de jour' : 'Day Estimate'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.airportTable.map((row, index) => (
                  <TableRow key={index} className={index === 0 ? 'bg-amber-50' : 'hover:bg-muted/20'}>
                    <TableCell className={index === 0 ? 'font-bold' : ''}>{row.zone}</TableCell>
                    <TableCell className="text-right font-bold text-primary">{row.price}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Link to={getRoute('/fares')} className="text-primary font-semibold hover:underline inline-flex items-center">
            {t.airportLink} <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </section>

      {/* Not Found Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-4">
            {t.notFoundTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            {t.notFoundDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90 font-bold">
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white font-bold">
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                {t.bookBtn}
              </a>
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <Button asChild size="lg" variant="ghost" className="text-muted-foreground hover:text-primary">
              <a href="#"><Smartphone className="w-5 h-5 mr-2" />{t.appStore}</a>
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-muted-foreground hover:text-primary">
              <a href="#"><Smartphone className="w-5 h-5 mr-2" />{t.googlePlay}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {t.availableTitle}
          </h2>
          <p className="text-accent-foreground/80 text-lg mb-8">
            {t.availableDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90 font-bold text-lg h-14 px-8">
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white font-bold text-lg h-14 px-8">
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                {t.bookBtn}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-8 bg-muted/50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
            <Link to={getRoute('/')} className="text-muted-foreground hover:text-primary">{t.footerHome}</Link>
            <Link to={getRoute('/airport')} className="text-muted-foreground hover:text-primary">{t.footerAirport}</Link>
            <Link to={getRoute('/fares')} className="text-muted-foreground hover:text-primary">{t.footerFares}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerServices}</Link>
            <Link to={getRoute('/faq')} className="text-muted-foreground hover:text-primary">{t.footerFaq}</Link>
            <Link to={getRoute('/contact')} className="text-muted-foreground hover:text-primary">{t.footerContact}</Link>
          </nav>
        </div>
      </section>
    </div>
  );
};

export default AreasPage;
